import { getEmitter } from '../../utils/emitter';
import { getDecodedIdToken, getIdToken, isAuthenticated } from '../../auth0-sdk';
import { ReconnectingWebSocket } from '@nktkas/rews';
import { BroadcastChannel, createLeaderElection, LeaderElector } from 'broadcast-channel';
import { FailoverMessage, OutgoingServerMessage, SessionState, CrossTabSyncMessage, IncomingServerMessage } from './types';

interface SessionManagerOptions {
  sessionManagerEndpoint: string;
  clientId: string;
  pingInterval?: number;
}

export class SessionManager {
  private ws: ReconnectingWebSocket | null = null;
  private readonly sessionManagerEndpoint: string;
  private readonly clientId: string;

  private _isInitialized = false;

  private sessionState: SessionState | null = null;

  private readonly pingInterval: number;
  private pingIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(options: SessionManagerOptions) {
    this.sessionManagerEndpoint = options.sessionManagerEndpoint;
    this.clientId = options.clientId;
    this.pingInterval = options.pingInterval ?? 30000;
  }

  async connect(): Promise<void> {
    if (this._isInitialized) return;

    await this.establishWebSocketConnection();

    this._isInitialized = true;
  }

  startMediaConsumption(): void {
    this.ws.send(JSON.stringify({ type: 'startMediaConsumption' } satisfies OutgoingServerMessage));
  }

  endMediaConsumption(): void {
    this.ws.send(JSON.stringify({ type: 'endMediaConsumption' } satisfies OutgoingServerMessage));
  }

  private applyState(state: SessionState) {
    if (state) {
      if (state.isInConcurrencyLimitedContext && !state.isActive) {
        getEmitter().emit('session:blocked');
      } else {
        getEmitter().emit('session:unblocked');
      }
    }
    this.sessionState = state;
  }

  private async establishWebSocketConnection(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    if (!isAuthenticated(this.clientId)) {
      throw new Error('User must be authenticated before initiating a session manager connection');
    }

    const urlProvider = () => {
      const url = new URL(`wss://${this.sessionManagerEndpoint}`);
      url.pathname = '/join';
      url.searchParams.set('token', getIdToken(this.clientId));
      return url.toString();
    };

    this.ws = new ReconnectingWebSocket(urlProvider, ['v1.mediahuis.session-manager+json'], {
      maxRetries: null,
      reconnectionDelay: 5000,
    });

    this.ws.onopen = () => {
      this.sessionState = null;
      this.startPing();
    };

    this.ws.onmessage = this.onWebsocketSessionMessage;

    this.ws.onclose = this.onWebsocketClose;

    this.ws.onerror = (error) => {
      console.error('Primary WebSocket error:', error);
    };
  }

  private reduceSessionStateMessage(prev: SessionState | null, message: IncomingServerMessage): SessionState | null {
    if (message.type !== 'sessionState') return prev;

    if (!prev || message.data.version > prev.version) {
      return message.data;
    }

    return prev;
  }

  private startPing(): void {
    this.stopPing();
    this.pingIntervalId = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send('ping');
      }
    }, this.pingInterval);
  }

  private stopPing(): void {
    if (this.pingIntervalId !== null) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }
  }

  private onWebsocketSessionMessage = async (event: MessageEvent) => {
    if (event.data === 'pong') {
      return;
    }

    const message = JSON.parse(event.data) as IncomingServerMessage;

    this.sessionState = this.reduceSessionStateMessage(this.sessionState, message);

    this.applyState(this.sessionState);
  };

  private onWebsocketClose = async (event: CloseEvent) => {
    console.log('Primary WebSocket closed:', event.code, event.reason);
    this.stopPing();
  };

  async shutdown(): Promise<void> {
    this.stopPing();

    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close(1000);
      }
      this.ws = null;
    }

    this.sessionState = null;
    this._isInitialized = false;
  }
}
